import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "flowbite-react";
import { FaRegCopy } from "react-icons/fa";
import {
  HiCheck,
  HiOutlineInformationCircle,
  HiOutlineRefresh,
} from "react-icons/hi";
import type { Invoice } from "../../models";
import type { AccountingConnection, AccountingInvoiceStatus } from "../../api";
import { getInvoiceIssuer } from "../../helpers/invoiceDelivery";
import {
  formatInvoiceMoney,
  getInvoiceSettlementAmounts,
} from "../../helpers/invoiceSettlement";
import { InvoiceStatusBadge } from "../Badges/InvoiceStatusBadge";
import { SimpleList } from "../SimpleList/SimpleList";
import { GroupedTabs, type ITabItem } from "../Tabs/GroupedTabs";
import {
  getInvoiceActivityCount,
  InvoiceActivity,
  InvoiceDeliverySummary,
} from "./InvoiceDeliveryTrace";
import { InvoiceDocument } from "./InvoiceDocument";
import { InvoiceLines } from "./InvoiceLines";

export interface InvoiceDetailsProps {
  invoice: Invoice | undefined;
  sendInvoice?: () => void;
  loadDocumentPreview?: () => Promise<string>;
  loadProviderDocument?: () => Promise<Blob>;
  accountingStatus?: AccountingInvoiceStatus | null;
  accountingConnection?: AccountingConnection | null;
  accountingStatusLoading?: boolean;
  accountingStatusRefreshing?: boolean;
  onRefreshAccountingStatus?: () => void;
}

const formatDate = (
  dateString: string | undefined,
  language: string,
  includeTime = false,
): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  const locale =
    language.startsWith("no") || language.startsWith("nb") ? "nb-NO" : "en-GB";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    ...(includeTime ? { timeStyle: "short" as const } : {}),
  }).format(date);
};

const customerName = (invoice: Invoice): string =>
  invoice.invoicee?.isCompany
    ? invoice.invoicee.name || "—"
    : [invoice.invoicee?.firstName, invoice.invoicee?.lastName]
        .filter(Boolean)
        .join(" ") ||
      invoice.invoicee?.name ||
      "—";

export const InvoiceDetails: FC<InvoiceDetailsProps> = ({
  invoice,
  loadDocumentPreview,
  loadProviderDocument,
  accountingStatus,
  accountingConnection,
  accountingStatusLoading = false,
  accountingStatusRefreshing = false,
  onRefreshAccountingStatus,
}) => {
  const { t, i18n } = useTranslation(["common", "invoice"]);
  const [hasCopied, setHasCopied] = useState(false);

  if (!invoice) return null;

  const issuer = getInvoiceIssuer(invoice);
  const isInvoiceBasis = invoice.issuanceMode === "accounting_provider";
  const isDirectAccountingIntegration =
    issuer === "accounting_provider" &&
    (accountingStatus?.mode === "integration" ||
      invoice.accountingMode === "integration" ||
      Boolean(accountingStatus?.providerKey ?? invoice.accountingProviderKey));
  const totalPrice = invoice.amounts?.totalAmount ?? 0;
  const locale =
    i18n.language.startsWith("no") || i18n.language.startsWith("nb")
      ? "nb-NO"
      : "en-GB";
  const formattedTotal = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
  }).format(totalPrice);
  const accountingAmountOutstanding =
    accountingStatus?.settlement?.amountOutstanding ??
    invoice.accountingAmountOutstanding;
  const accountingSettlementStatus =
    accountingStatus?.settlement?.status ?? invoice.accountingSettlementStatus;
  const parsedAccountingAmountOutstanding =
    accountingAmountOutstanding != null
      ? Number(accountingAmountOutstanding)
      : undefined;
  const hasKnownAccountingBalance =
    (accountingStatus?.mode === "integration" ||
      invoice.accountingMode === "integration") &&
    parsedAccountingAmountOutstanding !== undefined &&
    Number.isFinite(parsedAccountingAmountOutstanding);
  const accountingSettlementAmounts = hasKnownAccountingBalance
    ? getInvoiceSettlementAmounts(
        accountingStatus?.settlement?.amount ?? totalPrice,
        parsedAccountingAmountOutstanding,
      )
    : null;
  const formattedAccountingAmountOutstanding = accountingSettlementAmounts
    ? formatInvoiceMoney(
        accountingSettlementAmounts.remainingAmount,
        i18n.language,
      )
    : undefined;
  const formattedAccountingOverpaidAmount =
    accountingSettlementAmounts?.overpaidAmount
      ? formatInvoiceMoney(
          accountingSettlementAmounts.overpaidAmount,
          i18n.language,
        )
      : undefined;
  const formattedAccountingResolvedAmount = accountingSettlementAmounts
    ? formatInvoiceMoney(
        accountingSettlementAmounts.resolvedAmount,
        i18n.language,
      )
    : undefined;
  const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : undefined;
  const daysUntilDue =
    dueDate && !Number.isNaN(dueDate.getTime())
      ? Math.ceil((dueDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : undefined;
  const remainingText =
    daysUntilDue === undefined
      ? "—"
      : daysUntilDue >= 0
        ? `${daysUntilDue} ${t("common:days")}`
        : t("invoice:details.overdue");

  const overviewItems = [
    ...(invoice.lifecycleStatus === "discarded"
      ? [
          {
            title: t("invoice:discard.at"),
            value: formatDate(invoice.discardedAt, i18n.language, true),
          },
          { title: t("invoice:discard.by"), value: invoice.discardedBy || "—" },
        ]
      : []),
    {
      title: t(
        isInvoiceBasis
          ? "invoice:details.logibud_reference"
          : "invoice:details.invoice_number",
      ),
      value: invoice.invoiceNumber,
    },
    {
      title: t(
        isInvoiceBasis
          ? "invoice:details.basis_date"
          : "invoice:details.invoice_date",
      ),
      value: formatDate(invoice.invoiceDate, i18n.language),
    },
    ...(!isInvoiceBasis
      ? [
          {
            title: t("invoice:details.due_date"),
            value: formatDate(invoice.dueDate, i18n.language),
          },
        ]
      : []),
    ...(accountingStatus?.externalDocument?.kid
      ? [
          {
            title: t("invoice:details.kid"),
            value: accountingStatus.externalDocument.kid,
          },
        ]
      : []),
    {
      title: t("invoice:details.invoicer"),
      value: invoice.invoicer?.name || "—",
    },
    {
      title: t("invoice:delivery_trace.issuer"),
      value: t(`invoice:delivery_trace.issuers.${issuer}`),
    },
    {
      title: t("common:customer"),
      value: customerName(invoice),
    },
    ...(invoice.sourceOrders?.length ? invoice.sourceOrders.map(order => ({
      title: t("invoice:details.related_order"),
      value: `#${order.orderNumber}`,
      redirectPath: `/orders?id=${encodeURIComponent(order.orderId)}`,
    })) : [{
      title: t("invoice:details.related_order"),
      value: invoice.relatedOrderId ? t("common:open_order", "Open order") : "—",
      redirectPath: invoice.relatedOrderId ? `/orders?id=${encodeURIComponent(invoice.relatedOrderId)}` : undefined,
    }]),
    ...(invoice.yourReference
      ? [
          {
            title: t("common:project_number"),
            value: invoice.yourReference,
          },
        ]
      : []),
    ...(!isInvoiceBasis
      ? [
          {
            title: t("invoice:details.payment_means"),
            value:
              [
                invoice.paymentMeans?.accountNumber,
                invoice.paymentMeans?.iban,
                invoice.paymentMeans?.swift,
              ]
                .filter(Boolean)
                .join(" · ") || "—",
          },
        ]
      : []),
    ...(invoice.paidAmount > 0
      ? [
          {
            title: t("invoice:details.paid_amount"),
            value: `${invoice.paidAmount.toLocaleString(locale)} NOK`,
          },
        ]
      : []),
    ...(invoice.creditedAmount > 0
      ? [
          {
            title: t("invoice:details.credited_amount"),
            value: `${invoice.creditedAmount.toLocaleString(locale)} NOK`,
          },
        ]
      : []),
    {
      title: t("invoice:details.created_at"),
      value: formatDate(invoice.createdAt, i18n.language, true),
    },
    {
      title: t("invoice:details.last_updated"),
      value: formatDate(invoice.lastUpdated, i18n.language, true),
    },
  ];

  const tabs: ITabItem[] = [
    {
      id: "overview",
      label: t("invoice:tabs.overview"),
      content: (
        <div className="space-y-8 py-5">
          <section aria-labelledby="invoice-overview-heading">
            <h2
              id="invoice-overview-heading"
              className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted"
            >
              {t("invoice:tabs.invoice_details")}
            </h2>
            <SimpleList items={overviewItems} variant="compact" />
          </section>
          <section aria-labelledby="invoice-delivery-heading">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <h2
                  id="invoice-delivery-heading"
                  className="text-xs font-semibold uppercase tracking-wider text-muted"
                >
                  {t(
                    isDirectAccountingIntegration
                      ? "invoice:tabs.accounting"
                      : "invoice:tabs.delivery",
                  )}
                </h2>
                <Tooltip
                  content={
                    <p className="max-w-72 whitespace-normal text-left leading-5">
                      {t(
                        isDirectAccountingIntegration
                          ? "invoice:delivery_trace.accounting_overview_help"
                          : "invoice:delivery_trace.overview_help",
                      )}
                    </p>
                  }
                  placement="right"
                >
                  <button
                    type="button"
                    aria-label={t(
                      isDirectAccountingIntegration
                        ? "invoice:delivery_trace.accounting_overview_help"
                        : "invoice:delivery_trace.overview_help",
                    )}
                    className="rounded text-muted hover:text-brand focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    <HiOutlineInformationCircle className="h-4 w-4" />
                  </button>
                </Tooltip>
              </div>
              {(accountingStatus?.mode === "integration" ||
                accountingStatus?.providerKey) &&
              onRefreshAccountingStatus ? (
                <button
                  type="button"
                  onClick={onRefreshAccountingStatus}
                  disabled={accountingStatusRefreshing}
                  className="inline-flex items-center gap-1.5 rounded text-xs font-medium text-brand hover:underline disabled:cursor-wait disabled:opacity-60"
                >
                  <HiOutlineRefresh
                    className={`h-4 w-4 ${
                      accountingStatusRefreshing ? "animate-spin" : ""
                    }`}
                  />
                  {t("invoice:accounting_integration.refresh_status")}
                </button>
              ) : null}
            </div>
            <InvoiceDeliverySummary
              invoice={invoice}
              accountingStatus={accountingStatus}
              accountingConnection={accountingConnection}
              accountingStatusLoading={accountingStatusLoading}
            />
          </section>
        </div>
      ),
    },
    {
      id: "lines",
      label: t("invoice:tabs.lines"),
      count: invoice.items.length,
      content: <InvoiceLines invoice={invoice} />,
    },
    {
      id: "activity",
      label: t("invoice:tabs.activity"),
      count: getInvoiceActivityCount(invoice, accountingStatus),
      content: (
        <div className="py-3">
          <InvoiceActivity
            invoice={invoice}
            accountingStatus={accountingStatus}
          />
        </div>
      ),
    },
    {
      id: "document",
      label: t("invoice:tabs.document"),
      content: (
        <InvoiceDocument
          filename={`${isInvoiceBasis ? "invoice-basis" : "invoice"}-${invoice.invoiceNumber}.pdf`}
          loadPreview={loadDocumentPreview}
          providerDocument={
            accountingStatus?.externalDocument?.invoiceId &&
            loadProviderDocument
              ? {
                  filename: `tripletex-invoice-${
                    accountingStatus.externalDocument.invoiceNumber ||
                    accountingStatus.externalDocument.invoiceId
                  }.pdf`,
                  label: t(
                    "invoice:accounting_integration.download_provider_document",
                    { provider: "Tripletex" },
                  ),
                  download: loadProviderDocument,
                }
              : undefined
          }
        />
      ),
    },
  ];

  return (
    <article>
      <header className="pb-4 pt-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="text-xl font-semibold text-text sm:text-2xl">
            {t(
              isInvoiceBasis
                ? "invoice:details.invoice_basis"
                : "invoice:details.invoice_title",
            )}{" "}
            {invoice.invoiceNumber}
          </h1>
          <span className="text-sm text-muted sm:text-base">
            {customerName(invoice)}
          </span>
          <Tooltip content={hasCopied ? t("common:copied") : t("common:copy")}>
            <button
              type="button"
              aria-label={t("common:copy")}
              className="rounded-md p-1.5 text-secondary hover:bg-surface-hover hover:text-text focus-visible:ring-2 focus-visible:ring-brand"
              onClick={() => {
                void navigator.clipboard.writeText(
                  invoice.invoiceNumber?.toString() ?? "",
                );
                setHasCopied(true);
              }}
            >
              {hasCopied ? (
                <HiCheck className="h-5 w-5" />
              ) : (
                <FaRegCopy className="h-5 w-5" />
              )}
            </button>
          </Tooltip>
          <InvoiceStatusBadge
            invoice={invoice}
            accountingSettlementStatus={accountingStatus?.settlement?.status}
          />
        </div>

        <dl className="mt-2 flex flex-wrap gap-x-10 gap-y-3 py-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-muted">
              {t("invoice:pdf.sum_total")}
            </dt>
            <dd className="mt-0.5 text-lg font-semibold tabular-nums text-text">
              {formattedTotal} NOK
            </dd>
          </div>
          {formattedAccountingResolvedAmount !== undefined ? (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted">
                {t(
                  accountingSettlementStatus === "credited"
                    ? "invoice:details.credited_amount"
                    : "invoice:details.paid_amount",
                )}
              </dt>
              <dd className="mt-0.5 text-lg font-semibold tabular-nums text-text">
                {formattedAccountingResolvedAmount} NOK
              </dd>
            </div>
          ) : null}
          {formattedAccountingAmountOutstanding !== undefined ? (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted">
                {t("invoice:details.remaining_balance")}
              </dt>
              <dd className="mt-0.5 text-lg font-semibold tabular-nums text-text">
                {formattedAccountingAmountOutstanding} NOK
              </dd>
            </div>
          ) : null}
          {formattedAccountingOverpaidAmount !== undefined ? (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted">
                {t("invoice:details.overpaid_amount")}
              </dt>
              <dd className="mt-0.5 text-lg font-semibold tabular-nums text-text">
                {formattedAccountingOverpaidAmount} NOK
              </dd>
            </div>
          ) : null}
          {isInvoiceBasis ? (
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-muted">
                {t("invoice:details.basis_date")}
              </dt>
              <dd className="mt-0.5 text-lg font-semibold tabular-nums text-text">
                {formatDate(invoice.invoiceDate, i18n.language)}
              </dd>
            </div>
          ) : (
            <>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted">
                  {t("invoice:details.due_date")}
                </dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-text">
                  {formatDate(invoice.dueDate, i18n.language)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted">
                  {t("invoice:details.days_until_deadline")}
                </dt>
                <dd className="mt-0.5 text-lg font-semibold tabular-nums text-text">
                  {remainingText}
                </dd>
              </div>
            </>
          )}
        </dl>
      </header>

      <GroupedTabs
        key={invoice.id}
        tabs={tabs}
        initialTabId="overview"
        ariaLabel={t("invoice:tabs.aria_label")}
        variant="detail"
      />
    </article>
  );
};
